// App Routes Configuration for PlexiSystem 4.0
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { ProtectedRoute } from '../components/ProtectedRoute';

// Import pages - podstawowe strony ładujemy normalnie
import { LoginPage } from '../pages/LoginPage';
import { DevLoginPage } from '../pages/DevLoginPage';
import { AuthCallback } from '../pages/AuthCallback';
import AdminPanel from '../pages/AdminPanel';
import HomePage from '../pages/HomePage';
import Dashboard from '../pages/Dashboard';
import { OfferView } from '../pages/OfferView';
import { OfferAcceptance } from '../pages/OfferAcceptance';
import { PublicOffer } from '../pages/PublicOffer';
import { ClientPanel } from '../pages/ClientPanel';
import { Calculator as CalculatorPage } from '../components/quotation/Calculator';
import { ProductionPage } from '../pages/ProductionPage';
import { ProductDetailPage } from '../pages/ProductDetailPage';
import { PublicConfigurator } from '../pages/configurator/PublicConfigurator';
import { ClientsPage } from '../pages/ClientsPage';
import { ProductsPage } from '../pages/ProductsPage';
import { ReportsPage } from '../pages/ReportsPage';
import { AutomationSettings } from '../pages/AutomationSettings';
import { AIAssistant } from '../pages/AIAssistant';
import { KnowledgeBase } from '../pages/KnowledgeBase';
import { Marketplace } from '../pages/Marketplace';
import { MachiningParameters } from '../components/MachiningParameters';
import { QualityControl } from '../pages/QualityControl';
import { UserManagementPage } from '../pages/UserManagement';
import { UnauthorizedPage } from '../pages/UnauthorizedPage';
import Payroll from '../pages/Payroll';
import { TestPage } from '../pages/TestPage';
import TestChatbot from '../pages/TestChatbot';
import { PublicChatbot } from '../pages/PublicChatbot';
import { ChatbotSettings } from '../pages/ChatbotSettings';
import { ChatbotAnalytics } from '../pages/ChatbotAnalytics';
import SocialMediaPage from '../pages/SocialMediaPage';
import SocialMediaOAuthCallback from '../modules/social-media/components/OAuthCallback';
import AutomationPanel from '../pages/AutomationPanel';
import { NotificationsPage } from '../pages/NotificationsPage';
import TechnicalPage from '../pages/TechnicalPage';
import BendingCalculatorPage from '../pages/BendingCalculatorPage';
import ThermoformingCalculatorPage from '../pages/ThermoformingCalculatorPage';
import PackagingConfiguratorPage from '../pages/PackagingConfiguratorPage';
import GluingCalculatorPage from '../pages/GluingCalculatorPage';
import LaserCO2CalculatorPage from '../pages/LaserCO2CalculatorPage';
import PolishingCalculatorPage from '../pages/PolishingCalculatorPage';
import MaterialsDatabasePage from '../pages/MaterialsDatabasePage';
import { ProfitabilityDashboard } from '../modules/profitability/components/ProfitabilityDashboard';
import { PricingOptimizer } from '../modules/profitability/components/PricingOptimizer';
import SystemHealthCheckPage from '../pages/SystemHealthCheck';
import EnhancedDashboardPage from '../pages/EnhancedDashboardPage';
import CrystalCalcPage from '../pages/CrystalCalculatorPage';
import APISettingsPage from '../pages/APISettingsPage';
import GoogleAdsPage from '../pages/GoogleAdsPage';
import GoogleAdsCallback from '../modules/googleAds/GoogleAdsCallback';
import DatabaseCheck from '../pages/DatabaseCheck';
import OfferTestPage from '../pages/OfferTestPage';
import ModulesCenter from '../pages/ModulesCenter';
import AssistantDesigner from '../pages/ai/AssistantDesigner';
import GanttPage from '../pages/GanttPage';
import EngineeringPage from '../pages/EngineeringPage';
import EngineeringHistory from '../pages/EngineeringHistory';
import PresetsAdminPanel from '../admin/presets';
import PresetsPage from '../pages/PresetsPage';

// ⚡ LAZY LOADING - Ciężkie moduły ładujemy tylko gdy potrzebne
const Configurator3DPage = lazy(() => import('../pages/Configurator3DPage').then(module => ({ default: module.Configurator3DPage })));
const VisualizerPage = lazy(() => import('../pages/VisualizerPage').then(module => ({ default: module.VisualizerPage })));
const ExpositorDesignerPage = lazy(() => import('../pages/ExpositorDesignerPage'));

// Loading Component
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="relative">
        <div className="w-20 h-20 border-4 border-violet-500/20 rounded-full"></div>
        <div className="w-20 h-20 border-4 border-t-violet-500 rounded-full animate-spin absolute top-0"></div>
      </div>
      <p className="text-gray-400 mt-4 animate-pulse">Ładowanie PlexiSystem...</p>
    </div>
  </div>
);

// Loading Component dla modułów 3D
const Loading3D: React.FC = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="relative">
        <div className="w-20 h-20 border-4 border-purple-500/20 rounded-full"></div>
        <div className="w-20 h-20 border-4 border-t-purple-500 rounded-full animate-spin absolute top-0"></div>
      </div>
      <p className="text-gray-400 mt-4 animate-pulse">🎨 Ładowanie modułu 3D...</p>
      <p className="text-gray-500 text-sm mt-2">To może potrwać chwilę przy pierwszym uruchomieniu</p>
    </div>
  </div>
);

export const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Check if current route is public
  const isPublicRoute = 
    window.location.pathname.startsWith('/offer/accept/') || 
    window.location.pathname.startsWith('/oferta/') || 
    window.location.pathname.startsWith('/panel/') ||
    window.location.pathname.startsWith('/configurator/public/') ||
    window.location.pathname === '/login' ||
    window.location.pathname === '/dev-login' ||
    window.location.pathname === '/auth/success' ||
    window.location.pathname.startsWith('/auth/') ||
    window.location.pathname === '/chatbot';

  // Public routes
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
        <Route path="/dev-login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <DevLoginPage />} />
        <Route path="/auth/success" element={<AuthCallback />} />
        <Route path="/offer/accept/:shareId" element={<OfferAcceptance />} />
        <Route path="/oferta/:token" element={<PublicOffer />} />
        <Route path="/panel/:token" element={<ClientPanel />} />
        <Route path="/configurator/public/:token" element={<PublicConfigurator />} />
        <Route path="/chatbot" element={<PublicChatbot />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Protected routes with layout
  return (
    <AppLayout>
      <Routes>
        {/* Auth redirects */}
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Main routes */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/modules" element={<ModulesCenter />} />
        
        {/* Crystal UI Routes */}
        <Route path="/dashboard-new" element={<EnhancedDashboardPage />} />
        <Route path="/calculator-pro" element={<CrystalCalcPage />} />
        
        {/* Sales Hub */}
        <Route path="/calculator" element={
          <ProtectedRoute requiredModule="calculator" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec', 'operator', 'magazynier']}>
            <div className="max-w-6xl mx-auto">
              <h1 className="text-2xl font-bold mb-6 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                🧮 Kalkulator PRO
              </h1>
              <CalculatorPage onAddToOffer={(item) => { window.location.href = '/offer/new'; }} viewMode="salesperson" />
            </div>
          </ProtectedRoute>
        } />
        <Route path="/offers" element={
          <ProtectedRoute requiredModule="offers" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <HomePage />
          </ProtectedRoute>
        } />
        <Route path="/quotes" element={
          <ProtectedRoute requiredModule="offers" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <HomePage />
          </ProtectedRoute>
        } />
        <Route path="/offer/new" element={
          <ProtectedRoute requiredRoles={['admin', 'handlowiec']}>
            <OfferView />
          </ProtectedRoute>
        } />
        <Route path="/offer/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <OfferView />
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute requiredModule="clients" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <ClientsPage />
          </ProtectedRoute>
        } />
        
        {/* Digital Factory */}
        <Route path="/gantt" element={
          <ProtectedRoute requiredModule="gantt" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec']}>
            <GanttPage />
          </ProtectedRoute>
        } />
        <Route path="/production" element={
          <ProtectedRoute requiredModule="production" requiredRoles={['admin', 'kierownik_produkcji', 'operator']}>
            <ProductionPage />
          </ProtectedRoute>
        } />
        <Route path="/machining" element={
          <ProtectedRoute requiredModule="machining" requiredRoles={['admin', 'kierownik_produkcji', 'operator']}>
            <MachiningParameters />
          </ProtectedRoute>
        } />
        <Route path="/quality" element={
          <ProtectedRoute requiredModule="quality" requiredRoles={['admin', 'kierownik_produkcji', 'operator']}>
            <QualityControl />
          </ProtectedRoute>
        } />
        
        {/* Business */}
        <Route path="/profitability" element={
          <ProtectedRoute requiredModule="profitability" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec']}>
            <ProfitabilityDashboard />
          </ProtectedRoute>
        } />
        <Route path="/profitability/pricing" element={
          <ProtectedRoute requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec']}>
            <PricingOptimizer />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute requiredModule="reports" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec']}>
            <ReportsPage />
          </ProtectedRoute>
        } />
        
        {/* Innovation Lab */}
        <Route path="/ai/designer" element={
          <ProtectedRoute requiredModule="ai" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <AssistantDesigner />
          </ProtectedRoute>
        } />
        <Route path="/ai" element={
          <ProtectedRoute requiredModule="ai" requiredRoles={['admin', 'handlowiec']}>
            <AIAssistant />
          </ProtectedRoute>
        } />
        <Route path="/social-media" element={
          <ProtectedRoute requiredModule="social-media" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <SocialMediaPage />
          </ProtectedRoute>
        } />
        <Route path="/social-media/oauth/callback" element={
          <ProtectedRoute requiredModule="social-media" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <SocialMediaOAuthCallback />
          </ProtectedRoute>
        } />
        <Route path="/google-ads" element={
          <ProtectedRoute requiredModule="google-ads" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <GoogleAdsPage />
          </ProtectedRoute>
        } />
        <Route path="/google-ads/callback" element={
          <ProtectedRoute requiredModule="google-ads" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <GoogleAdsCallback />
          </ProtectedRoute>
        } />
        <Route path="/test-chatbot" element={
          <ProtectedRoute requiredModule="chatbot-settings" requiredRoles={['admin']}>
            <TestChatbot />
          </ProtectedRoute>
        } />
        <Route path="/chatbot-settings" element={
          <ProtectedRoute requiredModule="chatbot-settings" requiredRoles={['admin', 'handlowiec']}>
            <ChatbotSettings />
          </ProtectedRoute>
        } />
        <Route path="/chatbot-analytics" element={
          <ProtectedRoute requiredModule="chatbot-analytics" requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <ChatbotAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/automation" element={
          <ProtectedRoute requiredModule="automation" requiredRoles={['admin']}>
            <AutomationPanel />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute requiredModule="notifications" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec']}>
            <NotificationsPage />
          </ProtectedRoute>
        } />
        
        {/* Design Studio - LAZY LOADED 🚀 */}
        <Route path="/configurator3d" element={
          <ProtectedRoute requiredModule="configurator3d">
            <Suspense fallback={<Loading3D />}>
              <Configurator3DPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/visualizer" element={
          <ProtectedRoute requiredModule="visualizer">
            <Suspense fallback={<Loading3D />}>
              <VisualizerPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/expositor-designer" element={
          <ProtectedRoute requiredModule="expositor-designer">
            <Suspense fallback={<Loading3D />}>
              <ExpositorDesignerPage />
            </Suspense>
          </ProtectedRoute>
        } />
        
        {/* Knowledge Base */}
        <Route path="/knowledge" element={
          <ProtectedRoute requiredModule="knowledge">
            <KnowledgeBase />
          </ProtectedRoute>
        } />
        <Route path="/marketplace" element={
          <ProtectedRoute requiredModule="marketplace" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec', 'operator', 'magazynier']}>
            <Marketplace />
          </ProtectedRoute>
        } />
        
        {/* Technical Tools */}
        <Route path="/engineering" element={
          <ProtectedRoute requiredModule="engineering" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec']}>
            <EngineeringPage />
          </ProtectedRoute>
        } />
        <Route path="/engineering/history" element={
          <ProtectedRoute requiredModule="engineering" requiredRoles={['admin', 'kierownik_produkcji', 'handlowiec']}>
            <EngineeringHistory />
          </ProtectedRoute>
        } />
        <Route path="/materials-database" element={
          <ProtectedRoute requiredModule="materials-database">
            <MaterialsDatabasePage />
          </ProtectedRoute>
        } />
        <Route path="/laser-co2" element={<LaserCO2CalculatorPage />} />
        <Route path="/polishing-calculator" element={<PolishingCalculatorPage />} />
        <Route path="/technical" element={
          <ProtectedRoute requiredModule="technical">
            <TechnicalPage />
          </ProtectedRoute>
        } />
        <Route path="/bending-calculator" element={<BendingCalculatorPage />} />
        <Route path="/thermoforming-calculator" element={<ThermoformingCalculatorPage />} />
        <Route path="/packaging-configurator" element={<PackagingConfiguratorPage />} />
        <Route path="/gluing-calculator" element={<GluingCalculatorPage />} />
        
        {/* Admin Zone */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <AdminPanel />
          </ProtectedRoute>
        } />
        <Route path="/admin/presets" element={
          <ProtectedRoute requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji']}>
            <PresetsAdminPanel />
          </ProtectedRoute>
        } />
        <Route path="/templates" element={
          <ProtectedRoute requiredRoles={['admin', 'handlowiec', 'kierownik_produkcji', 'operator']}>
            <PresetsPage />
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <UserManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute requiredModule="settings" requiredRoles={['admin']}>
            <AutomationSettings />
          </ProtectedRoute>
        } />
        <Route path="/payroll" element={
          <ProtectedRoute requiredModule="payroll" requiredRoles={['admin', 'kierownik_produkcji']}>
            <Payroll />
          </ProtectedRoute>
        } />
        <Route path="/system-health" element={
          <ProtectedRoute requiredModule="system-health" requiredRoles={['admin']}>
            <SystemHealthCheckPage />
          </ProtectedRoute>
        } />
        <Route path="/api-settings" element={
          <ProtectedRoute requiredModule="api-settings" requiredRoles={['admin']}>
            <APISettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/test" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <TestPage />
          </ProtectedRoute>
        } />
        <Route path="/database-check" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DatabaseCheck />
          </ProtectedRoute>
        } />
        <Route path="/test-offers" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <OfferTestPage />
          </ProtectedRoute>
        } />
        
        {/* Other routes */}
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/product/:id" element={<ProductDetailPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        
        {/* Public offer routes - handled separately but included for completeness */}
        <Route path="/offer/accept/:shareId" element={<OfferAcceptance />} />
        <Route path="/oferta/:token" element={<PublicOffer />} />
        <Route path="/panel/:token" element={<ClientPanel />} />
        <Route path="/configurator/public/:token" element={<PublicConfigurator />} />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppLayout>
  );
};
